import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Trash } from "lucide-react";
import { api, fmtINR, apiError } from "../lib/api";
import { UNITS } from "../lib/modules";
import { useAuth } from "../context/AuthContext";
import { canWrite, canDelete } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const n = (v) => Number(v || 0);
const qtyf = (v) => (v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const TYPES = ["Finished Goods", "Raw Material", "Packaging Material"];
const emptyBom = () => ({ product_id: "", product_name: "", quantity: "", unit: "MT" });
const stockColor = (row) => {
  const cur = n(row.current_stock), mn = n(row.min_stock);
  if (cur <= 0) return "bg-red-100 text-red-700 border-red-200";
  if (cur <= mn) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-emerald-100 text-emerald-700 border-emerald-200";
};

export default function ProductsPage() {
  const { user } = useAuth();
  const canW = canWrite(user, "products");
  const canDel = canDelete(user, "products");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [bomRaw, setBomRaw] = useState([]);
  const [bomPack, setBomPack] = useState([]);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/products")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const rawOpts = useMemo(() => items.filter((p) => p.product_type === "Raw Material"), [items]);
  const packOpts = useMemo(() => items.filter((p) => p.product_type === "Packaging Material"), [items]);

  const openCreate = () => {
    setEditing(null);
    setForm({ status: "Active", unit: "MT", product_type: "Finished Goods", batch_required: false, opening_stock: 0, min_stock: 0 });
    setBomRaw([]); setBomPack([]);
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...row });
    setBomRaw((row.bom_raw || []).map((b) => ({ ...b })));
    setBomPack((row.bom_packaging || []).map((b) => ({ ...b })));
    setOpen(true);
  };

  const setBomLine = (list, setList, opts) => (i, key, val) => {
    setList((prev) => {
      const copy = prev.map((l) => ({ ...l }));
      copy[i][key] = val;
      if (key === "product_id") {
        const p = opts.find((x) => x.id === val);
        if (p) { copy[i].product_name = p.product_name; copy[i].unit = p.unit || "MT"; }
      }
      return copy;
    });
  };
  const setRawLine = setBomLine(bomRaw, setBomRaw, rawOpts);
  const setPackLine = setBomLine(bomPack, setBomPack, packOpts);

  const save = async () => {
    if (!form.product_name) { toast.error("Product Name is required"); return; }
    if (!form.product_type) { toast.error("Product Type is required"); return; }
    const hsn = String(form.hsn_code || "").trim();
    if (!hsn || !/^[0-9]{4,8}$/.test(hsn)) { toast.error("HSN must be 4 to 8 digits"); return; }
    const clean = (l) => l.filter((b) => b.product_id && n(b.quantity) > 0).map((b) => ({ product_id: b.product_id, product_name: b.product_name, quantity: n(b.quantity), unit: b.unit }));
    const payload = {
      ...form,
      purchase_rate: n(form.purchase_rate), sales_rate: n(form.sales_rate),
      gst_rate: n(form.gst_rate), opening_stock: n(form.opening_stock), min_stock: n(form.min_stock),
      bom_raw: form.product_type === "Finished Goods" ? clean(bomRaw) : [],
      bom_packaging: form.product_type === "Finished Goods" ? clean(bomPack) : [],
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(`Product ${editing ? "updated" : "created"}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/products/${deleteId}`); toast.success("Deleted"); setDeleteId(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    let out = items;
    if (query) { const q = query.toLowerCase(); out = out.filter((p) => ["sku", "product_name", "grade", "category", "product_type", "hsn_code"].some((k) => String(p[k] || "").toLowerCase().includes(q))); }
    if (typeFilter !== "all") out = out.filter((p) => p.product_type === typeFilter);
    return out;
  }, [items, query, typeFilter]);

  const BomTable = ({ title, rows, setRows, setter, opts, testid }) => (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title} <span className="text-slate-400 normal-case font-normal">(per 1 {form.unit || "unit"})</span></label>
        <Button data-testid={`add-${testid}-btn`} size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => setRows([...rows, emptyBom()])}><Plus className="w-3.5 h-3.5" /> Add</Button>
      </div>
      <div className="border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50 text-left text-slate-500"><th className="px-2 py-2 min-w-[200px]">Material</th><th className="px-2 py-2 w-28">Qty / Unit</th><th className="px-2 py-2 w-24">Unit</th><th className="px-2 py-2 w-8"></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={4} className="px-2 py-3 text-center text-slate-400">No lines. Click Add.</td></tr>
              : rows.map((l, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">
                    <Select value={l.product_id || ""} onValueChange={(v) => setter(i, "product_id", v)}>
                      <SelectTrigger data-testid={`${testid}-${i}-material`} className="bg-white h-8"><SelectValue placeholder="Select material" /></SelectTrigger>
                      <SelectContent>{opts.map((p) => <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5"><Input data-testid={`${testid}-${i}-qty`} type="number" step="0.0001" value={l.quantity} onChange={(e) => setter(i, "quantity", e.target.value)} className="h-8 bg-white" /></td>
                  <td className="px-2 py-1.5"><Select value={l.unit || "MT"} onValueChange={(v) => setter(i, "unit", v)}><SelectTrigger className="bg-white h-8"><SelectValue /></SelectTrigger><SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></td>
                  <td className="px-2 py-1.5"><button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600"><Trash className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const field = (label, key, opts = {}) => (
    <div className="space-y-1">
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}{opts.req && <span className="text-red-500"> *</span>}</label>
      <Input data-testid={`field-${key}`} type={opts.type || "text"} step={opts.step} min={opts.min} value={form[key] ?? ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="bg-white" />
      {opts.hint && <span className="block text-[10px] text-slate-400">{opts.hint}</span>}    </div>
  );

  return (
    <div data-testid="module-products">
      <div className="mb-5"><h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Products</h1>
        <p className="text-sm text-slate-500 mt-0.5">Product master · finished goods, raw &amp; packaging materials with BOM recipes</p></div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <p className="text-sm text-slate-500">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-products" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 w-full sm:w-52 bg-white" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="type-filter-products" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          {canW && <Button data-testid="add-products-btn" onClick={openCreate} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Plus className="w-4 h-4" /><span className="hidden sm:inline">Add New</span></Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
            {["Code", "Product", "Type", "Grade", "HSN", "Category", "Unit", "Purchase Rate", "Sales", "Stock", "Min", "Status", ""].map((h, i) => (
              <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${h === "" ? "text-right" : ""}`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={13} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={13} className="px-4 py-10 text-center text-slate-400">No products found</td></tr>
              : filtered.map((row) => (
                <tr key={row.id} data-testid={`row-products-${row.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-600">{row.sku || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-800">{row.product_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{row.product_type || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{row.grade || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-600">{row.hsn_code || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{row.category || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{row.unit}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono tabular-nums text-slate-900" data-testid={`product-rate-${row.id}`}>
                    {fmtINR(row.effective_purchase_rate ?? row.purchase_rate)}
                    <div className={`text-[10px] font-sans ${row.rate_source === "inventory_actual_unit_cost" ? "text-emerald-700" : row.rate_source === "reference_rate" ? "text-amber-700" : "text-slate-400"}`} data-testid={`product-rate-source-${row.id}`}>
                      {row.rate_source === "inventory_actual_unit_cost" ? "Inventory Actual Unit Cost" : row.rate_source === "reference_rate" ? "Reference Rate" : "No cost"}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono tabular-nums text-slate-900">{fmtINR(row.sales_rate)}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><Badge variant="outline" className={`rounded-sm font-mono tabular-nums ${stockColor(row)}`}>{qtyf(row.current_stock)}</Badge></td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono tabular-nums text-slate-500">{qtyf(row.min_stock)}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`rounded-sm capitalize ${String(row.status || "Active").toLowerCase() === "inactive" ? "bg-red-100 text-red-700 border-red-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>{row.status || "Active"}</Badge></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      {canW && <Button data-testid={`edit-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-[#0B132B]" onClick={() => openEdit(row)}><Pencil className="w-4 h-4" /></Button>}
                      {canDel && <Button data-testid={`delete-${row.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-red-600" onClick={() => setDeleteId(row.id)}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit" : "New"} Product</DialogTitle><DialogDescription className="sr-only">Product master form</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {field("Product Code / SKU", "sku")}
            {field("Product Name", "product_name", { req: true })}
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product Type *</label>
              <Select value={form.product_type || ""} onValueChange={(v) => setForm({ ...form, product_type: v })}>
                <SelectTrigger data-testid="field-product_type" className="bg-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            {field("Grade", "grade")}
            {field("Category", "category")}
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unit *</label>
              <Select value={form.unit || "MT"} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="field-unit" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select></div>
            {field("Default / Reference Purchase Rate (₹)", "purchase_rate", { type: "number", min: "0", hint: n(editing?.actual_unit_cost) > 0 ? `Not used — Inventory Actual Unit Cost ${fmtINR(editing.actual_unit_cost)} / ${editing.unit || ""} is in force` : "Used only until this product has actual purchase bills" })}
            {field("Sales Rate (₹)", "sales_rate", { type: "number", min: "0" })}
            {field("GST Rate (%)", "gst_rate", { type: "number", min: "0" })}
            {field("HSN Code", "hsn_code", { req: true })}
            {field("Opening Stock", "opening_stock", { type: "number", step: "0.001", min: "0" })}
            {field("Minimum Stock Level", "min_stock", { type: "number", step: "0.001", min: "0" })}
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Batch Required</label>
              <Select value={form.batch_required ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, batch_required: v === "yes" })}>
                <SelectTrigger data-testid="field-batch_required" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
              </Select></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</label>
              <Select value={form.status || "Active"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="field-status" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select></div>
          </div>

          {form.product_type === "Finished Goods" && (
            <div className="mt-4 border-t border-slate-200 pt-3" data-testid="bom-section">
              <h3 className="text-sm font-semibold text-slate-800 font-head">Bill of Materials (Recipe)</h3>
              <p className="text-xs text-slate-500">Define consumption per 1 unit of this finished product. Production auto-scales by quantity produced.</p>
              <BomTable title="Raw Materials" rows={bomRaw} setRows={setBomRaw} setter={setRawLine} opts={rawOpts} testid="bomraw" />
              <BomTable title="Packaging Materials" rows={bomPack} setRows={setBomPack} setter={setPackLine} opts={packOpts} testid="bompack" />
            </div>
          )}

          <div className="space-y-1 mt-3"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <Textarea data-testid="field-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white" /></div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-btn" onClick={save} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete product?</AlertDialogTitle><AlertDialogDescription>This removes the product and its opening stock movement. Consider marking Inactive instead.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-btn" onClick={doDelete} className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
